import { CompanyConfigResourceApi } from '@root/api';
import { authService } from './auth.service';
import {
    CompanyStatusState,
    createDefaultState,
    evaluateCompanyStatusResults
} from './company_status.utils';

const companyConfigApi = new CompanyConfigResourceApi();

export const companyStatusState: CompanyStatusState = createDefaultState();

const applyStatusState = (state: CompanyStatusState): boolean => {
    Object.assign(companyStatusState, state);
    return state.statusResults.length > 0;
};

let pollingTimeout: ReturnType<typeof setTimeout> | null = null;
let pollGeneration = 0;
let pollingActive = false;

const isUnauthorizedError = (error: unknown): boolean =>
    (error as { response?: { status?: number } })?.response?.status === 401;

export const stopStatusPolling = (): void => {
    pollGeneration += 1;
    pollingActive = false;
    if (pollingTimeout) {
        clearTimeout(pollingTimeout);
        pollingTimeout = null;
    }
};

export const startStatusPolling = (): void => {
    if (pollingActive) {
        return;
    }
    pollingActive = true;
    retrieveStatus();
};

export const retrieveStatus = (): void => {
    if (pollingTimeout) {
        clearTimeout(pollingTimeout);
        pollingTimeout = null;
    }

    if (!authService.isLoggedIn()) {
        stopStatusPolling();
        applyStatusState(createDefaultState());
        return;
    }

    const generation = pollGeneration;

    // Re-polled every 30-60s below for the whole session. A tick that never reaches the
    // server resets to the default state and retries, so the miss is already handled;
    // reporting it would file the user's connection as a dashboard defect.
    companyConfigApi.getCompanyConfigStatus({ backgroundPoll: true })
        .then((response) => {
            if (generation !== pollGeneration || !authService.isLoggedIn()) {
                return;
            }

            const hasIssues = applyStatusState(evaluateCompanyStatusResults(response.data));
            pollingTimeout = setTimeout(retrieveStatus, hasIssues ? 30000 : 60000);
        })
        .catch((error) => {
            if (generation !== pollGeneration) {
                return;
            }

            if (!authService.isLoggedIn() || isUnauthorizedError(error)) {
                stopStatusPolling();
                applyStatusState(createDefaultState());
                return;
            }

            applyStatusState(createDefaultState());
            pollingTimeout = setTimeout(retrieveStatus, 30000);
        });
};

export const companyStatusService = {
    ...companyStatusState,
    retrieveStatus,
    startStatusPolling,
    stopStatusPolling
};
